/**
 * Footer SEO Route Links — crawlable internal links grid
 * Adds 20+ high-value internal links in the footer for SEO authority distribution.
 */
import { Link } from "react-router-dom";
import { useLocalizedPath } from "@/hooks/useLocalizedPath";
import { getTopGlobalRoutes } from "@/data/seoRoutes";
import { getCountryHubs, getDestinationHubs } from "@/data/seoHubs";
import { useTranslation } from "react-i18next";

const POPULAR_COUNTRY_SLUGS = [
  "france-to-morocco",
  "belgium-to-morocco",
  "netherlands-to-morocco",
  "spain-to-morocco",
  "germany-to-morocco",
  "italy-to-morocco",
];

const POPULAR_DEST_SLUGS = [
  "shipping-to-casablanca",
  "shipping-to-tangier",
  "shipping-to-rabat",
  "shipping-to-marrakech",
  "shipping-to-nador",
  "shipping-to-oujda",
];

export default function FooterRouteLinks() {
  const { localePath } = useLocalizedPath();
  const { t } = useTranslation();
  const topRoutes = getTopGlobalRoutes(8);

  return (
    <div className="border-t border-white/[0.06]">
      <div className="container py-8">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-xs">
          {/* Popular Routes */}
          <div>
            <h5 className="text-white/60 font-semibold uppercase tracking-wider mb-3 text-[10px]">
              {t('footerLinks.popularRoutes')}
            </h5>
            <ul className="space-y-1.5">
              {topRoutes.slice(0, 6).map(r => (
                <li key={r.slug}>
                  <Link
                    to={localePath(`/shipping-routes/${r.slug}`)}
                    className="text-white/40 hover:text-white/70 transition-colors"
                  >
                    {r.fromCity} → {r.toCity}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Country Corridors */}
          <div>
            <h5 className="text-white/60 font-semibold uppercase tracking-wider mb-3 text-[10px]">
              {t('footerLinks.countryCorridors')}
            </h5>
            <ul className="space-y-1.5">
              {POPULAR_COUNTRY_SLUGS.map(slug => {
                const countryKey = slug.replace("-to-morocco", "").replace(/-/g, " ").replace(/\b\w/g, c => c.toUpperCase());
                const label = t('footerLinks.corridor', { country: t(`countries.${countryKey}`, countryKey) });
                return (
                  <li key={slug}>
                    <Link
                      to={localePath(`/shipping-routes/${slug}`)}
                      className="text-white/40 hover:text-white/70 transition-colors"
                    >
                      {label}
                    </Link>
                  </li>
                );
              })}
            </ul>
          </div>

          {/* Destinations */}
          <div>
            <h5 className="text-white/60 font-semibold uppercase tracking-wider mb-3 text-[10px]">
              {t('footerLinks.shipToMorocco')}
            </h5>
            <ul className="space-y-1.5">
              {POPULAR_DEST_SLUGS.map(slug => {
                const city = slug.replace("shipping-to-", "").replace(/\b\w/g, c => c.toUpperCase());
                return (
                  <li key={slug}>
                    <Link
                      to={localePath(`/shipping-routes/${slug}`)}
                      className="text-white/40 hover:text-white/70 transition-colors"
                    >
                      {t('footerLinks.shippingTo', { city })}
                    </Link>
                  </li>
                );
              })}
            </ul>
          </div>

          {/* Quick Links & Guides */}
          <div>
            <h5 className="text-white/60 font-semibold uppercase tracking-wider mb-3 text-[10px]">
              {t('footerLinks.quickLinks')}
            </h5>
            <ul className="space-y-1.5">
              <li><Link to={localePath("/shipping-routes")} className="text-white/40 hover:text-white/70 transition-colors">{t('footerLinks.allShippingRoutes')}</Link></li>
              <li><Link to={localePath("/courier-to-morocco")} className="text-white/40 hover:text-white/70 transition-colors">{t('footerLinks.courierToMorocco')}</Link></li>
              <li><Link to={localePath("/courier-from-france")} className="text-white/40 hover:text-white/70 transition-colors">{t('footerLinks.courierFrom', { country: t('countries.France') })}</Link></li>
              <li><Link to={localePath("/courier-from-spain")} className="text-white/40 hover:text-white/70 transition-colors">{t('footerLinks.courierFrom', { country: t('countries.Spain') })}</Link></li>
              <li><Link to={localePath("/courier-from-belgium")} className="text-white/40 hover:text-white/70 transition-colors">{t('footerLinks.courierFrom', { country: t('countries.Belgium') })}</Link></li>
              <li><Link to={localePath("/courier-from-netherlands")} className="text-white/40 hover:text-white/70 transition-colors">{t('footerLinks.courierFrom', { country: t('countries.Netherlands') })}</Link></li>
              <li><Link to={localePath("/courier-from-germany")} className="text-white/40 hover:text-white/70 transition-colors">{t('footerLinks.courierFrom', { country: t('countries.Germany') })}</Link></li>
              <li><Link to={localePath("/courier-from-italy")} className="text-white/40 hover:text-white/70 transition-colors">{t('footerLinks.courierFrom', { country: t('countries.Italy') })}</Link></li>
              <li><Link to={localePath("/courier-from-united-kingdom")} className="text-white/40 hover:text-white/70 transition-colors">{t('footerLinks.courierFrom', { country: t('countries.United Kingdom') })}</Link></li>
              <li><Link to={localePath("/quote")} className="text-white/40 hover:text-white/70 transition-colors">{t('footerLinks.getFreeQuote')}</Link></li>
              <li><Link to={localePath("/how-it-works")} className="text-white/40 hover:text-white/70 transition-colors">{t('footerLinks.howItWorks')}</Link></li>
              <li><Link to={localePath("/for-couriers")} className="text-white/40 hover:text-white/70 transition-colors">{t('footerLinks.becomeCourier')}</Link></li>
              <li><Link to={localePath("/guides/customs-morocco-europe")} className="text-white/40 hover:text-white/70 transition-colors">{t('footerLinks.customsGuide')}</Link></li>
              <li><Link to={localePath("/guides/shipping-large-items")} className="text-white/40 hover:text-white/70 transition-colors">{t('footerLinks.largeItemsGuide')}</Link></li>
              <li><Link to={localePath("/guides/diaspora-shipping")} className="text-white/40 hover:text-white/70 transition-colors">{t('footerLinks.diasporaGuide')}</Link></li>
              <li><Link to={localePath("/faq")} className="text-white/40 hover:text-white/70 transition-colors">{t('nav.faq')}</Link></li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}
